import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CampaignConfigurationModalComponent } from './campaign-configuration-modal.component';
import { AccountGroupService } from '../account-group';
import { firstValueFrom, Observable, switchMap } from 'rxjs';
import { take } from 'rxjs/operators';
import { Address } from '@vendasta/businesses';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { NapUpdateOperation, UpdateOperations } from '@vendasta/listing-profile-common';
import { ListingProfileService } from '@vendasta/listing-profile-common';

@Injectable({
  providedIn: 'root',
})
export class CampaignConfigurationService {
  constructor(
    private dialog: MatDialog,
    private accountGroupService: AccountGroupService,
    private http: HttpClient,
    private listingProfileService: ListingProfileService,
  ) {}

  async isProperlyConfigured(): Promise<boolean> {
    const accountGroup = await firstValueFrom(this.accountGroupService.currentAccountGroup$.pipe());

    // Check if zip code exists and is not empty
    return !!accountGroup.zip && accountGroup.zip.trim() !== '';
  }

  openConfigurationModal(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const dialogRef = this.dialog.open(CampaignConfigurationModalComponent, {
        width: '600px',
        disableClose: true,
        autoFocus: false, // Disable auto-focus behavior
      });

      dialogRef.afterClosed().subscribe((result) => {
        // Return the result (true if address was updated, false or undefined otherwise)
        resolve(result);
      });
    });
  }

  getBusinessAddress(): Observable<Address> {
    return this.accountGroupService.currentAccountGroup$.pipe(
      take(1),
      map((accountGroup) => {
        return {
          company: accountGroup.companyName || '',
          address1: accountGroup.address || '',
          address2: accountGroup.address2 || '',
          city: accountGroup.city || '',
          country: accountGroup.country || '',
          province: accountGroup.state || '',
          zip: accountGroup.zip || '',
        } as Address;
      }),
    );
  }

  updateBusinessAddress(addressData: any): Observable<any> {
    return this.accountGroupService.currentAccountGroupId$.pipe(
      take(1),
      switchMap((accountGroupId) => {
        const operations = new UpdateOperations();

        operations.addUpdateOperation(
          new NapUpdateOperation({
            companyName: addressData.company,
            address: addressData.address1,
            address2: addressData.address2,
            city: addressData.city,
            state: addressData.province?.code || addressData.province,
            zip: addressData.zip,
            country: addressData.country?.code || addressData.country,
          }),
        );

        return this.listingProfileService.bulkUpdate(accountGroupId, operations).pipe(
          catchError((error) => {
            console.error('Error updating address:', error);
            throw error;
          }),
          // After successfully updating the address, refresh the account group data
          switchMap(() => {
            return this.accountGroupService.refreshCurrentAccountGroup();
          }),
        );
      }),
    );
  }
}
